import React, { ReactNode, createContext, useEffect, useRef, useState } from "react";

import { DataTransformer } from "@emed-labs/platform-utility-data-transformer";
import { getCookie, setCookie } from "cookies-next";
import { Language } from "@emed-labs/ftf-datatypes/lib/Misc/Language";
import { NextPageContext } from "next/types";
import { useRouter } from "next/router";

interface LanguageContext {
	language: Language;
	// make sure not to call this method after procedure load
	changeLanguage: (lang: Language) => void;
}

export const LanguageContext = createContext<LanguageContext>({} as LanguageContext);
LanguageContext.displayName = "LanguageContext";

export const LanguageProvider = (props: { children?: ReactNode; language: Language }) => {
	const [languageState, setLanguage] = useState<Language>(props.language || LanguageUtils.getUserLanguage());
	const Router = useRouter();
	const langRef = useRef<Language>();

	useEffect(() => {
		let _language: Language = langRef.current;

		if (!_language) {
			if (Router.query.lang) {
				const _lang = LanguageUtils.validateLanguage(Router.query.lang as Language);
				if (_lang) {
					langRef.current = _lang;
					_language = _lang;
				}
			} else if (props.language) {
				_language = props.language;
			} else {
				_language = LanguageUtils.getUserLanguage();
			}
		}

		if (_language) {
			LanguageUtils.setUserLanguage(_language);
			setLanguage(_language);
		}
	}, [props.language, Router.query.lang]);

	const changeLanguage = (lang: Language) => {
		langRef.current = lang;
		LanguageUtils.setUserLanguage(lang);
		setLanguage(lang);
	};

	const value: LanguageContext = {
		language: languageState,
		changeLanguage
	};

	return <LanguageContext.Provider value={value}>{props.children}</LanguageContext.Provider>;
};
export default LanguageProvider;

export const DEFAULT_LANGUAGE = Language.ENGLISH;

export class LanguageUtils {
	static getUserLanguage(ctx?: NextPageContext): Language {
		/**
		 * getCookie works for server and client side
		 */
		const langCookie = getCookie("ftf-lang", { req: ctx?.req })?.toString();
		let lang: Language =
			(langCookie as Language) ??
			//window.navigator.language parens required or error when server side
			(typeof window !== "undefined" ? (window.navigator.language as Language) : DEFAULT_LANGUAGE);

		if (ctx?.req) {
			/**
			 * SERVER SIDE
			 */

			/**
			 * 1: Look for 'lang' in query param
			 * 2: look for 'ftf-lang' cookie
			 * 3: look for headers 'accept-language' ( can be tested with Devtools 'Sensors': locale )
			 * 3: default to English ( 'en' )
			 */
			lang =
				((ctx.query?.lang ?? ctx.query?.hsLang) as Language) ||
				langCookie ||
				(ctx.req.headers["accept-language"] as any) ||
				DEFAULT_LANGUAGE;
		}

		/**
		 * Ensure language is valid, or return the default language
		 */
		return LanguageUtils.validateLanguage(lang) || DEFAULT_LANGUAGE;
	}

	static validateLanguage(lang: Language | string): Language | false {
		for (const language of Object.values(Language)) {
			/**
			 * we want to be sure "en-us" resolves to "en", for example
			 */
			if (lang.indexOf(language) === 0) return language;
		}
		return false;
	}

	/**
	 * Read the language from URL, cookie, or headers
	 * and setCookie and return language.
	 *
	 * Works server and client side
	 *
	 * @param ctx
	 * @returns
	 */
	static setupUserLanguage(ctx?: NextPageContext): Language {
		const langCookie = getCookie("ftf-lang", { req: ctx?.req })?.toString();
		const lang = LanguageUtils.getUserLanguage(ctx);
		if (langCookie !== lang) {
			LanguageUtils.setUserLanguage(lang, ctx);
		}

		return lang;
	}

	static setUserLanguage(lang: Language, ctx?: NextPageContext) {
		let host = typeof window !== "undefined" ? window?.location?.host : ctx?.req?.headers?.host;
		let domain = host && host.startsWith("localhost") ? "localhost" : ".emed.com";
		/**
		 * Works server and client side
		 */
		setCookie("ftf-lang", lang, {
			req: ctx?.req,
			res: ctx?.res,
			domain,
			path: "/"
		});
		return lang;
	}

	static transformUISchema(data: any, language: string = LanguageUtils.getUserLanguage()) {
		return DataTransformer.transform(data, {
			language: language
		});
	}
}
